import { getSetGroups } from '../../../utils/setGroups';
import { KG, convertLoadText, getExerciseUnit, normalizeUnit, unitLabel } from '../../../utils/units';

/**
 * Display rules shared by the student's video screens — the library cards, the
 * "Terminé" rows and the full-screen detail view.
 *
 * They live here rather than in `pages/StudentVideoLibrary.jsx` so the detail
 * view can reuse the *exact* same formatting the cards use instead of growing a
 * second, slightly-different formatter next to it.
 */

/**
 * Review state of a video.
 *
 * `getStudentVideos` (backend) normalizes everything to a legacy `status`:
 * 'completed' once the coach has given feedback (text, audio, or an explicit
 * "marked complete"), 'pending' otherwise. Older rows can still carry
 * 'reviewed'.
 */
export const isReviewed = (video) => video?.status === 'completed' || video?.status === 'reviewed';

/**
 * Filmed in a session the student has not validated yet: the coach does not
 * receive it until then (backend `session_not_validated`), so it is not
 * « En attente du coach ».
 */
export const isSessionNotValidated = (video) => !isReviewed(video) && video?.session_not_validated === true;

/** 'reviewed' | 'unvalidated' | 'awaiting' — the key of STATUS_PILL. */
export const videoReviewStatus = (video) => {
  if (isReviewed(video)) return 'reviewed';
  return isSessionNotValidated(video) ? 'unvalidated' : 'awaiting';
};

/** The single status-pill mapping — the cards and the detail view share it. */
export const STATUS_PILL = {
  reviewed: 'bg-[rgba(47,160,100,0.15)] text-kaiylo-done',
  awaiting: 'bg-[rgba(212,132,90,0.1)] text-kaiylo-accent',
  unvalidated: 'bg-[rgba(255,255,255,0.08)] text-[rgba(255,255,255,0.6)]'
};

// 'unvalidated' lives in the studentVideos namespace: callers import
// i18n/studentVideosNamespace (StudentVideoLibrary, StudentVideoDetailView).
export const getStatusLabel = (status, t) =>
  status === 'reviewed'
    ? t('dashboard:student.videos.status_reviewed')
    : status === 'awaiting'
      ? t('dashboard:video_library.status.to_feedback')
      : status === 'unvalidated'
        ? t('studentVideos:session_not_validated')
        : null;

/**
 * Weight / reps / RPE for one video. The video row itself may only carry part
 * of it, so the coach's prescription in `assignment.workout_session.exercises`
 * fills the gaps and decides whether the load reads as a weight or as an RPE
 * (`useRir`).
 */
export const getVideoWeightAndReps = (video) => {
  let weight = video.weight || video.target_weight || video.requested_weight;
  let reps = video.reps || video.target_reps || video.requested_reps;
  let rpe = 0;
  // Colonne numérique workout_videos.weight : toujours en kg (unité canonique).
  let weightUnit = KG;

  if (video.assignment?.workout_session?.exercises) {
    const exerciseName = video.exercise_name;
    const setNumber = video.set_number || 1;

    for (const exercise of video.assignment.workout_session.exercises) {
      if (exercise.name === exerciseName) {
        const set = exercise.sets?.[setNumber - 1];
        if (exercise.useRir) {
          // RPE mode: the coach's target lives in set.weight; the recorded
          // weight is not what's displayed.
          rpe = set?.weight || set?.target_weight || 0;
          weight = 0;
        } else if (!weight && set) {
          weight = set.weight || set.target_weight;
          weightUnit = getExerciseUnit(exercise);
        }
        if (set) reps = reps || set.reps || set.target_reps;
        break;
      }
    }
  }

  return { weight: weight || 0, reps: reps || 0, rpe, weightUnit };
};

// Same rule as SessionOfDayCard: a coach-typed load like "BW" already carries
// its own unit.
const hasUnitSuffix = (weight) => !/[a-zA-Z]/.test(String(weight ?? ''));

/**
 * "5reps" + "@40kg" for one video, in the student's own unit (`displayUnit`,
 * kg by default — see utils/units.js), through the canonical set grouper
 * (utils/setGroups.js) rather than a third bespoke formatter. A video is one
 * set, so the grouper returns a single group — it is used for its empty/zero
 * normalisation, which is exactly what keeps "0reps" off the card.
 */
export const buildSetText = (video, t, displayUnit = KG) => {
  const { weight, reps, rpe, weightUnit } = getVideoWeightAndReps(video);
  const to = normalizeUnit(displayUnit);
  const load = rpe > 0 ? rpe : weight ? convertLoadText(weight, weightUnit, to) : weight;
  const [group] = getSetGroups([{ reps, weight: load }]);
  const hasReps = group && group.reps !== 0 && group.reps !== '' && group.reps != null;
  const hasLoad = group && group.weight !== 0 && group.weight !== '' && group.weight != null;

  return {
    repsText: hasReps ? t('dashboard:student.videos.reps_short', { reps: group.reps }) : '',
    loadText: !hasLoad
      ? ''
      : rpe > 0
        ? ` RPE ${group.weight}`
        : ` @${group.weight}${hasUnitSuffix(group.weight) ? unitLabel(to) : ''}`
  };
};

/**
 * "Série 1/3" for one video.
 *
 * `set_number` is always present on the payload (getStudentVideos defaults it
 * to 1). `total_sets` is derived server-side from the coach's plan and is null
 * when the video cannot be matched to a planned exercise — hence the second
 * key, which drops the "/N" instead of printing a made-up total.
 */
export const buildSetLabel = (video, t) => {
  const current = video.set_number;
  if (!current) return '';
  const total = video.total_sets;
  return total
    ? t('dashboard:video_detail.series_index', { current, total })
    : t('dashboard:video_detail.series_index_no_total', { current });
};

/**
 * "1/4" — the bare set index for the detail view's "N° de série" row. Digits
 * only, so it needs no locale of its own; `null` when the payload has no set
 * number at all, and the "/N" is dropped when `total_sets` is null.
 */
export const buildSetIndexValue = (video) => {
  const current = video?.set_number;
  if (!current) return null;
  return video.total_sets ? `${current}/${video.total_sets}` : String(current);
};

/**
 * The planned exercise this video belongs to, from the coach's session plan.
 * It is where the coach's per-exercise instruction and the student's own
 * per-exercise comment are stored (`workout_sessions.exercises` JSON).
 */
export const getPlannedExercise = (video) =>
  video?.assignment?.workout_session?.exercises?.find((ex) => ex.name === video.exercise_name) ||
  null;

/**
 * The coach's written instruction for that exercise.
 *
 * Two coach-authored fields exist on the plan's exercise: `coachNote` (the
 * explicit note added from ExerciseSettingsPanel, rendered as a tooltip on the
 * coach side) and `notes` (the older per-exercise note input). Production data
 * has both, `notes` far more widely, so the explicit note wins and `notes` is
 * the fallback. Null when the coach wrote neither — the row is then omitted.
 */
export const getCoachInstruction = (video) => {
  const exercise = getPlannedExercise(video);
  const note = exercise?.coachNote ?? exercise?.coach_note ?? exercise?.notes;
  const text = (note || '').trim();
  return text || null;
};

/**
 * The student's own comment on that exercise.
 *
 * Legacy `workout_videos` rows carry it in their `comment` column; the TUS
 * `videos` flow never writes one into `metadata`, so for those it comes from
 * the plan's exercise, where the session execution writes it back
 * (`student_comment`). Null when there is none — the row is then omitted.
 */
export const getStudentExerciseComment = (video) => {
  const exercise = getPlannedExercise(video);
  const comment =
    video?.comment ||
    exercise?.comment ||
    exercise?.studentComment ||
    exercise?.student_comment ||
    exercise?.previous_student_comment;
  const text = (comment || '').trim();
  return text || null;
};

// ── Shared layout constants for the student's video screens ─────────────────

/**
 * Fluid page gutter: Figma's 26px on a 412px frame, floored so a 320px screen
 * keeps a usable 16px margin. The same value the dashboard and the planning
 * screen use — it lives here so the library page and both detail views read it
 * from one place instead of redeclaring it each time.
 */
export const PAGE_X = 'px-[clamp(16px,6.31vw,26px)]';

/**
 * Air above every student page title, below the status bar (whose own height
 * is already on <body> via env(safe-area-inset-top), see src/index.css). One
 * value for all screens: they used to range from 12 to 20px, which put the
 * titles too close to the top on a real phone.
 */
export const PAGE_TOP = 'pt-7';
export const PAGE_TOP_PX = 28;

/**
 * Clearance for the floating bottom nav plus the home-indicator inset.
 *
 * The nav is a `position: fixed` pill 51px tall sitting at
 * `env(safe-area-inset-bottom) + 12px`, so it covers the bottom ~63px of the
 * viewport (~97px on a notched iPhone) and its 25px glow a little more. Every
 * scrollable student screen reserves this, and the two full-screen detail
 * views reserve it themselves so they stay correct wherever they are mounted.
 *
 * Frozen so a caller cannot mutate the shared style object.
 */
export const BOTTOM_CLEARANCE = Object.freeze({
  paddingBottom: 'calc(100px + env(safe-area-inset-bottom, 0px))'
});

/** "1:07" from a media element's own clock. `null` when there is no usable value. */
export const formatTime = (seconds) => {
  if (!Number.isFinite(seconds) || seconds < 0) return null;
  const total = Math.floor(seconds);
  return `${Math.floor(total / 60)}:${String(total % 60).padStart(2, '0')}`;
};

/** Shown until `loadedmetadata` lands — never a rendered `NaN`. */
export const TIME_PLACEHOLDER = '--:--';
